import { Component, type ReactNode } from "react";

import { ErrorPage } from "@/components/error/ErrorPage";
import { attemptChunkReload, isChunkLoadError } from "@/lib/chunkReload";
import { captureError } from "@/lib/sentry";

/**
 * App-level React error boundary.
 *
 * Without this, any render-time error throws into React's reconciler
 * and replaces the entire app with a blank page. With it, the
 * customer sees the standard error screen, the error is reported to
 * Sentry (when configured + consented), and the rest of the app shell
 * stays interactive.
 *
 * We capture errors at two levels:
 *
 * 1. **Root** (around `<RouterProvider>`) — catastrophic failures the
 *    router can't catch itself, e.g. a router config error or a throw
 *    from a provider mounted above it.
 * 2. **Route** (`errorElement` → `components/error/RouteErrorPage`) —
 *    everything thrown inside the route tree. React Router's own
 *    boundary lives inside `<RouterProvider>` and catches those first,
 *    so they never reach this class; the route boundary is where they
 *    are actually handled, and it keeps the surrounding shell chrome.
 *
 * Class component — function components can't catch render errors
 * because hooks have no equivalent of `componentDidCatch`. The
 * pattern stays standard React.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional render override for the fallback. Defaults to the
   * standard error screen. Pass a custom element for a smaller
   * fallback (e.g. "this section couldn't load, the rest of the page
   * is fine").
   */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  eventId?: string;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(
    error: Error,
    info: { componentStack: string },
  ): void {
    // Defence-in-depth chunk-load recovery for throws that reach this
    // boundary rather than the route one — Vite's ``vite:preloadError``
    // is the primary path. Rate-limited to once per tab, so a
    // genuinely missing asset still surfaces the screen on retry.
    if (isChunkLoadError(error) && attemptChunkReload()) {
      return;
    }

    void captureError(error, info.componentStack).then((eventId) => {
      if (eventId) this.setState({ eventId });
    });

    // Also write to the console so dev sees the stack without
    // hunting through Sentry.
    // eslint-disable-next-line no-console
    console.error("Render-time error captured by ErrorBoundary:", error);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <ErrorPage
            kind="unknown"
            detail={{
              message: this.state.error?.message,
              stack: this.state.error?.stack,
              eventId: this.state.eventId,
            }}
          />
        )
      );
    }
    return this.props.children;
  }
}
